import React, { useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import { useResponsive } from '../../hooks/useResponsive';

interface GroupMatchLite {
  id: string;
  location: string;
  date: string;
  time: string;
}

interface Props {
  isOpen: boolean;
  groupId: string;
  upcomingMatches: GroupMatchLite[];
  onClose: () => void;
  onCreated: () => void;
  showNotification: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json',
});

const inputStyle = (mobile: boolean): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: mobile ? '10px' : '12px',
  background: '#2C2F33',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 8,
  color: theme.colors.text,
  fontSize: '16px',
});

export const CreatePollModal: React.FC<Props> = ({ isOpen, groupId, upcomingMatches, onClose, onCreated, showNotification }) => {
  const { isMobile } = useResponsive();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['Voy', 'No voy']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [syncMatchId, setSyncMatchId] = useState('');
  const [joinOptionIdx, setJoinOptionIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setQuestion(''); setOptions(['Voy', 'No voy']); setAllowMultiple(false);
    setSyncMatchId(''); setJoinOptionIdx(0);
  };

  const setOption = (i: number, val: string) => setOptions((prev) => prev.map((o, idx) => (idx === i ? val : o)));
  const addOption = () => options.length < 10 && setOptions((prev) => [...prev, '']);
  const removeOption = (i: number) => {
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
    if (joinOptionIdx >= i && joinOptionIdx > 0) setJoinOptionIdx((x) => x - 1);
  };

  const handleSubmit = async () => {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim()) { showNotification('Escribí la pregunta', 'error'); return; }
    if (clean.length < 2) { showNotification('Cargá al menos 2 opciones', 'error'); return; }
    setSubmitting(true);
    try {
      const payloadOptions = options
        .map((label, idx) => ({ label: label.trim(), joinsMatch: !!syncMatchId && !allowMultiple && idx === joinOptionIdx }))
        .filter((o) => o.label);
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/polls`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          question: question.trim(),
          options: payloadOptions,
          allowMultiple,
          matchId: syncMatchId || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showNotification(data.error || 'No se pudo crear la encuesta', 'error'); return; }
      showNotification('Encuesta creada', 'success');
      reset();
      onClose();
      onCreated();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 12 }}
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        style={{ background: theme.colors.surface, borderRadius: 16, padding: isMobile ? 16 : 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${theme.colors.border}` }}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h2 style={{ margin: '0 0 14px 0', color: theme.colors.text, fontSize: 19, fontWeight: 800 }}>📊 Nueva encuesta</h2>

        <label style={{ display: 'block', color: theme.colors.text, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Pregunta</label>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, 300))}
          placeholder="Ej: ¿Quién juega el sábado?"
          style={inputStyle(isMobile)}
        />

        <label style={{ display: 'block', color: theme.colors.text, fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>Opciones</label>
        {options.map((o, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input value={o} onChange={(e) => setOption(i, e.target.value.slice(0, 100))} placeholder={`Opción ${i + 1}`} style={inputStyle(isMobile)} />
            {options.length > 2 && (
              <button type="button" onClick={() => removeOption(i)} style={{ background: 'transparent', border: 'none', color: '#EF5350', cursor: 'pointer', fontSize: 18, padding: 6 }}>✕</button>
            )}
          </div>
        ))}
        {options.length < 10 && (
          <button type="button" onClick={addOption} style={{ background: 'transparent', border: `1px dashed ${theme.colors.border}`, color: theme.colors.primary, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + Agregar opción
          </button>
        )}

        {/* Sincronizar con un partido */}
        {upcomingMatches.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', color: theme.colors.text, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              🔗 Sincronizar con un partido (opcional)
            </label>
            <select value={syncMatchId} onChange={(e) => setSyncMatchId(e.target.value)} style={inputStyle(isMobile)}>
              <option value="">No sincronizar</option>
              {upcomingMatches.map((m) => (
                <option key={m.id} value={m.id}>{m.location} · {m.date} {m.time}</option>
              ))}
            </select>
            {syncMatchId && !allowMultiple && (
              <div style={{ marginTop: 8 }}>
                <span style={{ color: theme.colors.textSecondary, fontSize: 12 }}>¿Qué opción significa "Voy" (anota al partido)?</span>
                <select value={joinOptionIdx} onChange={(e) => setJoinOptionIdx(Number(e.target.value))} style={{ ...inputStyle(isMobile), marginTop: 4 }}>
                  {options.map((o, i) => (
                    <option key={i} value={i}>{o.trim() || `Opción ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            )}
            {syncMatchId && allowMultiple && (
              <div style={{ color: '#F59E0B', fontSize: 12, marginTop: 4 }}>La sincronización con el partido requiere opción única (desactivá "multi-opción").</div>
            )}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, color: theme.colors.text, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} />
          Permitir elegir varias opciones
        </label>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={{ flex: 1, padding: 14, borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: 'transparent', color: theme.colors.text, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} style={{ flex: 2, padding: 14, borderRadius: 10, border: 'none', background: theme.colors.primary, color: theme.colors.background, fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}>
            {submitting ? 'Creando…' : 'Crear encuesta'}
          </button>
        </div>
      </div>
    </div>
  );
};
